import React, {useMemo, useState, useEffect, Key} from 'react';
import {SelectionList, Modal, FlexRow, Text} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {Size} from '@forecast-it/design-system/dist/forecast-v2/components/feedback/modal/Modal';
import styled from 'styled-components';

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const SelectionListContainer = styled(SelectionList.RowContainer as any)`
	height: 490px;
`;

export interface EyeOption {
	name: string;
	displayName?: string;
	translationId?: string;
	disabled?: boolean;
	checked: boolean;
	nestedOptions?: EyeOption[];
}

export type ShowHideColumnsModalProps = {
	eyeOptions: EyeOption[];
	onEyeOptionsChange: (eyeOptions: EyeOption[]) => void;
	closeModal: () => void;
};

const MAX_ALLOWED_LEVELS = 3;
const treeHeight = (options?: EyeOption[]) =>
	options?.reduce(function (maxHeight, option) {
		return Math.max(maxHeight, treeHeight(option.nestedOptions) + 1);
	}, 1) ?? 0;

const ShowHideColumnsModal = ({
	eyeOptions: originalEyeOptions,
	onEyeOptionsChange,
	closeModal,
}: ShowHideColumnsModalProps): JSX.Element => {
	const {formatMessage} = useIntl();

	const [eyeOptions, setEyeOptions] = useState(originalEyeOptions);
	const [optionLevels, setOptionLevels] = useState([eyeOptions]);
	const maxLevel = useMemo(() => Math.min(treeHeight(originalEyeOptions), MAX_ALLOWED_LEVELS), originalEyeOptions);
	const [openedOptions, setOpenedOptions] = useState(['', '']);

	useEffect(() => {
		if (maxLevel === 2) {
			setOptionLevels([eyeOptions, []]);
		}
		if (maxLevel === 3) {
			setOptionLevels([eyeOptions, [], []]);
		}
	}, [maxLevel]);

	const toggleNestedOptions = (option: EyeOption, checkedStatus: boolean) => {
		option.nestedOptions?.forEach(nestedOption => {
			nestedOption.checked = !checkedStatus;
			if (nestedOption.nestedOptions) {
				toggleNestedOptions(nestedOption, checkedStatus);
			}
		});
	};

	const toggleOptions = (eyeOption: EyeOption) => {
		if (eyeOption) {
			if (eyeOption.nestedOptions) {
				toggleNestedOptions(eyeOption, eyeOption.checked);
			}
			eyeOption.checked = !eyeOption.checked;
		}
	};
	const toggleParentOption = (option: EyeOption, nestedOptions: EyeOption[]) => {
		if (option && nestedOptions?.every(nestedOption => nestedOption.checked === false)) {
			option.checked = false;
		} else if (option && nestedOptions?.every(nestedOption => nestedOption.checked === true)) {
			option.checked = true;
		}
	};

	const findOption = (options: EyeOption[], key: Key) => {
		return options.find(option => option.name === key);
	};

	const toggleChecked = (key: Key, level: number) => {
		setEyeOptions(prevEyeOptions => {
			if (level === 0) {
				const eyeOption = findOption(prevEyeOptions, key);
				eyeOption && toggleOptions(eyeOption);
			} else if (level === 1) {
				const option = findOption(prevEyeOptions, openedOptions[0]);
				const nestedOptions = option?.nestedOptions;
				const eyeOption = nestedOptions && findOption(nestedOptions, key);
				eyeOption && toggleOptions(eyeOption);
				option && nestedOptions && toggleParentOption(option, nestedOptions);
			} else if (level === 2) {
				const option = findOption(prevEyeOptions, openedOptions[0]);
				const firstNestedOptions = option?.nestedOptions;
				const firstNestedOption = firstNestedOptions && findOption(firstNestedOptions, openedOptions[1]);
				const secondNestedOptions = firstNestedOption?.nestedOptions;
				const eyeOption = secondNestedOptions && findOption(secondNestedOptions, key);
				eyeOption && toggleOptions(eyeOption);
				firstNestedOption && secondNestedOptions && toggleParentOption(firstNestedOption, secondNestedOptions);
				option && firstNestedOptions && toggleParentOption(option, firstNestedOptions);
			}
			return [...prevEyeOptions];
		});
	};

	const confirm = () => {
		onEyeOptionsChange(eyeOptions);
		closeModal();
	};

	const size = maxLevel === 1 ? Size.EXTRA_SMALL : maxLevel === 2 ? Size.MEDIUM : Size.EXTRA_LARGE;

	const toggleOpened = (key: Key, level: number) => {
		if (level === 0) {
			const eyeOption = findOption(eyeOptions, key);
			if (eyeOption && eyeOption.nestedOptions) {
				setOptionLevels(prevOptionLevels => {
					const newOptionLevels = [...prevOptionLevels];
					newOptionLevels[1] = openedOptions[0] === eyeOption.name ? [] : eyeOption.nestedOptions ?? [];
					if (newOptionLevels.length === 3) {
						newOptionLevels[2] = [];
					}
					return newOptionLevels;
				});
				setOpenedOptions(prevOpenedOptions => {
					const newOpenedOptions = [...prevOpenedOptions];
					newOpenedOptions[0] = newOpenedOptions[0] === eyeOption.name ? '' : eyeOption.name ?? [];
					newOpenedOptions[1] = '';
					return newOpenedOptions;
				});
			}
		} else if (level === 1) {
			const nestedOptions = findOption(eyeOptions, openedOptions[0])?.nestedOptions;
			const eyeOption = nestedOptions && findOption(nestedOptions, key);
			if (eyeOption && eyeOption.nestedOptions) {
				setOptionLevels(prevOptionLevels => {
					const newOptionLevels = [...prevOptionLevels];
					newOptionLevels[2] = openedOptions[1] === eyeOption.name ? [] : eyeOption.nestedOptions ?? [];
					return newOptionLevels;
				});
				setOpenedOptions(prevOpenedOptions => {
					const newOpenedOptions = [...prevOpenedOptions];
					newOpenedOptions[1] = newOpenedOptions[1] === eyeOption.name ? '' : eyeOption.name;
					return newOpenedOptions;
				});
			}
		}
	};

	return (
		<Modal size={size} onCloseModal={closeModal}>
			<Modal.Title>
				<FormattedMessage id="change_view.show_hide_columns" />
			</Modal.Title>
			<Modal.Content>
				<FormattedMessage id="change_view.show_hide_columns.description" />
				<SelectionListContainer>
					{optionLevels.map((options, level) => (
						<SelectionList key={level} aria-label={'Eye Options - Level ' + level} openedOptions={openedOptions}>
							{options.map(({name, translationId, checked, nestedOptions}) => {
								const label = formatMessage({id: translationId});
								return (
									<SelectionList.Item key={name} textValue={label} data-cy={name + '-item'}>
										<FlexRow justifyContent={'space-between'}>
											<Text color={checked ? 'main' : 'auxiliary'}>{label}</Text>
											<FlexRow justifyContent={'flex-end'}>
												<SelectionList.ShowHideIcon
													onClick={() => toggleChecked(name, level)}
													checked={checked}
													data-cy={name}
												/>
												{nestedOptions && (
													<SelectionList.OpenNestedOptionIcon
														onClick={() => toggleOpened(name, level)}
														data-cy={name + '-open'}
													/>
												)}
											</FlexRow>
										</FlexRow>
									</SelectionList.Item>
								);
							})}
						</SelectionList>
					))}
				</SelectionListContainer>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={confirm}>
					<FormattedMessage id={'common.confirm'} />
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal}>
					<FormattedMessage id={'common.close'} />
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default ShowHideColumnsModal;
